import Image from 'next/image';

import { SectionWrapper } from '@/components/atomic/nuclei';
import { DEVICE_SURFACE, staticMediaStoreBaseURL } from '@/config/common';
import { PATH_TO_GROWTH_STEPS } from '@/config/landingPage';
import { GetUiDeviceSurface } from '@/lib/screenResolution';

const PartToGrowthBodyWeb = () => (
  <div className='relative mt-8 sm:mt-10'>
    <div className='hidden xl:flex 2xl:hidden absolute inset-0 w-full h-full top-1/4 px-12 xl:px-24'>
      {[...Array(PATH_TO_GROWTH_STEPS.length - 1)].map((_, i) => (
        <div
          className='relative w-full h-6 md:h-8 lg:h-10 xl:h-12'
          key={i}
        >
          <Image
            alt='connector'
            aria-hidden='true'
            className='object-contain w-full h-full'
            fill
            src={`${staticMediaStoreBaseURL}/landing-page/path-to-growth/steps-connector.svg`}
          />
        </div>
      ))}
    </div>
    <div className='relative z-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6'>
      {PATH_TO_GROWTH_STEPS.map((step) => (
        <article
          className='rounded-2xl border border-pink-100 shadow-sm p-6 sm:p-7 md:p-8 text-center hover:shadow-md transition'
          key={step.id}
        >
          <div className='text-[10px] sm:text-xs md:text-sm font-semibold uppercase bg-brand-gradient bg-clip-text text-transparent'>
            {step.label}
          </div>

          <div className='mx-auto mt-4 sm:mt-5'>
            <div className='relative h-14 w-14 sm:h-16 sm:w-16 md:h-[100px] md:w-[100px] mx-auto'>
              <Image
                alt={step.title}
                className='object-contain'
                fill
                priority={step.id === 1}
                sizes='(max-width: 767px) 64px, (max-width: 1279px) 80px, 96px'
                src={step.icon}
              />
            </div>
          </div>

          <div className='mt-4 sm:mt-5 text-lg md:text-xl font-semibold text-neutral-900'>
            {step.title}
          </div>
          <div className='mt-3 sm:mt-4 text-sm md:text-sm sm:text-base text-neutral-600 leading-7'>
            {step.desc}
          </div>
        </article>
      ))}
    </div>
  </div>
);

const PartToGrowthBodyMobile = () => (
  <div className='relative md:hidden mt-8 sm:mt-10'>
    <div className='relative z-10 grid grid-cols-2 gap-x-4 gap-y-10'>
      {PATH_TO_GROWTH_STEPS.map(({ icon, id, label, title }, idx) => {
        const right = idx % 2 === 1;
        return (
          <div
            className={[
              'col-span-1 flex flex-col items-center text-center',
              right ? 'col-start-2' : 'col-start-1'
            ].join(' ')}
            key={id}
          >
            <div className='text-[10px] sm:text-xs md:text-sm font-semibold uppercase bg-brand-gradient bg-clip-text text-transparent'>
              {label}
            </div>
            <div className='relative h-12 w-12 sm:h-16 sm:w-16'>
              <Image
                alt={title}
                className='object-contain'
                fill
                sizes='48px'
                src={icon}
                unoptimized
              />
            </div>
            <div className='mt-2 text-sm font-semibold text-neutral-900'>
              {title}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const PathToGrowth = () => {
  const surface = GetUiDeviceSurface();
  const partToGrowthBodyMap = {
    [DEVICE_SURFACE.WEB]: PartToGrowthBodyWeb,
    [DEVICE_SURFACE.MOBILE]: PartToGrowthBodyMobile
  };

  const PartToGrowthBody = partToGrowthBodyMap[surface];

  return (
    <SectionWrapper>
      <div className='text-center max-w-4xl mx-auto'>
        <div
          className={`text-2xl xs:text-2xl sm:text-2xl md:text-4xl font-extrabold leading-tight text-neutral-800 text-stroke-thin md:text-stroke-normal`}
        >
          Your Path to Growth in{' '}
          <span className='bg-brand-gradient bg-clip-text text-transparent'>
            4 Simple Steps
          </span>
        </div>
        <div className='mt-3 sm:mt-4 text-sm sm:text-base md:text-xl lg:text-xl text-neutral-600'>
          From onboarding to earning, we’ve made it fast, friendly, and
          effortless.
        </div>
        <div className='mt-4 sm:mt-6 flex items-center justify-center gap-2'>
          <span className='h-1 w-20 sm:w-24 rounded-full bg-brand-gradient' />
          <span className='h-1 w-6 rounded-full bg-brand-gradient' />
        </div>
      </div>
      <PartToGrowthBody />
    </SectionWrapper>
  );
};

export default PathToGrowth;
